import Seo from './Seo'
import {relatedMarketingPages} from './seoConstants'
import {articleSchema, faqSchema} from './schema'
import {CtaSection, FaqSection, InlineText, RelatedLinks, ScreenshotGrid} from './seoSections'
import type {GuideData, GuideTable} from './seoTypes'
import './SeoPage.css'

const GuideTableView = ({table}: {table: GuideTable}) => (
  <div className="seo-table-wrap">
    <table className="seo-table">
      <caption>{table.caption}</caption>
      <thead>
        <tr>
          {table.headers.map((header, i) => (
            <th key={header} scope="col" className={i === 0 ? 'seo-col-feature' : undefined}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row) => (
          <tr key={row.join('|')}>
            {row.map((cell, i) => (
              <td key={i} className={i === 0 ? 'seo-col-feature' : undefined}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

// Reusable long-form guide article, driven by a GuideData object. Emits unique
// SEO head + Article & FAQPage JSON-LD, structured <h2> sections with optional
// tables, an FAQ, and CTAs into the free tools.
const GuideArticle = ({data}: {data: GuideData}) => {
  const related = relatedMarketingPages(data.canonicalPath)

  return (
    <div className="seo-root" data-prerender-ready>
      <Seo
        title={data.title}
        description={data.description}
        canonicalPath={data.canonicalPath}
        ogImage={data.ogImage}
        ogType="article"
        jsonLd={[articleSchema(data.title, data.description, data.canonicalPath), faqSchema(data.faq)]}
      />
      <div className="seo-grid" aria-hidden />

      <main className="seo-page">
        <header className="seo-hero">
          <span className="seo-eyebrow">
            <span className="seo-eyebrow-dot" />
            {data.eyebrow}
          </span>
          <h1 className="seo-h1">{data.h1}</h1>
          {data.intro.map((para, i) => (
            <p key={i} className="seo-lede">
              <InlineText text={para} />
            </p>
          ))}
          <div className="seo-meta">
            <span>Updated {data.updated}</span>
            <span>{data.readingTime}</span>
          </div>
        </header>

        {data.sections.map((section) => (
          <section key={section.heading} className="seo-section">
            <h2 className="seo-h2">{section.heading}</h2>
            {section.body.map((para, i) => (
              <p key={i} className="seo-p">
                <InlineText text={para} />
              </p>
            ))}
            {section.bullets && (
              <ul className="seo-list seo-mt">
                {section.bullets.map((item) => (
                  <li key={item}>
                    <InlineText text={item} />
                  </li>
                ))}
              </ul>
            )}
            {section.table && (
              <div className="seo-mt">
                <GuideTableView table={section.table} />
              </div>
            )}
          </section>
        ))}

        {data.screenshots.length > 0 && (
          <section className="seo-section">
            <h2 className="seo-h2">Build these in Ecranify</h2>
            <ScreenshotGrid shots={data.screenshots} variant="contain" />
          </section>
        )}

        <FaqSection title="Frequently asked questions" faq={data.faq} />

        <RelatedLinks title="Related guides & comparisons" links={related} />

        <CtaSection cta={data.cta} secondaryLinks={data.ctaSecondaryLinks} />
      </main>
    </div>
  )
}

export default GuideArticle
